import { FC, useMemo } from "react";
import { Building2, LucideIcon, User } from "lucide-react";
import { TargetedSubscriberEnum } from "api/pricing";
import { useAppSelector } from "app/hooks";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { selectFormulasContext } from "../../slices/pricing/selectors";
import { ContractFunnel } from "../../types/interfaces";

interface FormulaSubscriberBadgeProps {
  contractFunnel: ContractFunnel;
}

/**
 * Who a fixed formula is for, as a badge beside the card title: the company when it targets a
 * legal entity, the manager (named when known) when it targets an individual on a company's file.
 */
export const FormulaSubscriberBadge: FC<FormulaSubscriberBadgeProps> = ({ contractFunnel }) => {
  const { t } = useDynamicTranslation([NsKey.Pricing]);
  const formulasContext = useAppSelector(selectFormulasContext);

  const subscriber = useMemo((): { icon: LucideIcon; label: string; name?: string; className: string } | null => {
    const targetedSubscriber = contractFunnel.product.targetedSubscriber;
    if (targetedSubscriber === TargetedSubscriberEnum.LegalEntity) {
      return { icon: Building2, label: t("PRICING.FORMULAS.FOR_COMPANY"), className: "bg-primary/10 text-primary" };
    }
    if (targetedSubscriber === TargetedSubscriberEnum.Individual && formulasContext?.insuredIsLegalEntity) {
      const manager = formulasContext.manager;
      const name = [manager?.firstName, manager?.lastName].filter(Boolean).join(' ').trim();
      return { icon: User, label: t("PRICING.FORMULAS.FOR_MANAGER"), name: name || undefined, className: "bg-primary/10 text-primary" };
    }
    return null;
  }, [contractFunnel.product.targetedSubscriber, formulasContext, t]);

  if (!subscriber) {
    return null;
  }

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${subscriber.className}`}>
      <subscriber.icon className="h-3.5 w-3.5 flex-none" strokeWidth={2} />
      <span>{subscriber.label}</span>
      {subscriber.name && <span className="font-bold">{subscriber.name}</span>}
    </span>
  );
};
